import {DateTimeFormatter, DayOfWeek, LocalDate, Month} from '@js-joda/core';
import {Locale} from '@js-joda/locale_en-us';
import {Hidden} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import ListSubheader from '@material-ui/core/ListSubheader';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TextField from '@material-ui/core/TextField';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import TimeVarianceAuthority from "@tracker/shared/src/Scheduling/Components/TimeVarianceAuthority";
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
    listSubheader: {
        position: 'static',
    },
}));

export type FilterSearchType = 'people' | 'projects';

export type FilterSearch = {
    query : string;
    type : FilterSearchType;
};

type Props = {
    className ?: string;
    startDate : LocalDate;
    onStartDateChange : (startDate : LocalDate) => void;
    filterSearch : FilterSearch;
    onFilterSearchChange : (filterSearch : FilterSearch) => void;
};

const dateFormatter = DateTimeFormatter.ofPattern('M/d');
const monthFormatter = DateTimeFormatter.ofPattern('MMMM').withLocale(Locale.US);

type DateOption = {
    label : string;
    value : Month;
    weeks : Array<{
        value : string;
        label : string;
    }>;
};

const ScheduleFilter : React.FC<Props> = ({
    className,
    startDate,
    onStartDateChange,
    filterSearch,
    onFilterSearchChange,
} : Props) => {
    const classes = useStyles();
    const [query, setQuery] = useState(filterSearch.query);
    const queryField = useRef<HTMLInputElement>();

    useEffect(() => setQuery(filterSearch.query), [filterSearch.query]);

    const startDateValue = useMemo(() => startDate.toString(), [startDate]);

    const firstDate = useMemo(() => {
        const firstDate = LocalDate.now();

        if (firstDate.dayOfWeek() === DayOfWeek.SUNDAY) {
            return firstDate.minusWeeks(10);
        }

        return firstDate.minusDays(firstDate.dayOfWeek().value() + 70);
    }, []);

    const dateOptions = useMemo(() => {
        const dateOptions : DateOption[] = [];
        let month : DateOption | null = null;

        for (let weekNo = 0; weekNo < 27; ++weekNo) {
            const date = firstDate.plusWeeks(weekNo);

            if (!month || month.value !== date.month()) {
                month = {label: date.format(monthFormatter), value: date.month(), weeks: []};
                dateOptions.push(month);
            }

            month.weeks.push({
                value: date.toString(),
                label: date.format(dateFormatter),
            });
        }

        return dateOptions;
    }, [firstDate]);

    const debouncedSearch = useCallback(debounce(
        (query : string, type : FilterSearchType) => onFilterSearchChange({type, query}),
        400
    ), [onFilterSearchChange]);

    const handleQueryChange = (query : string) => {
        setQuery(query);
        debouncedSearch(query, filterSearch.type);
    };

    const handleQueryKeyDown = (event : React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            handleQueryChange('');
        }
    };

    useEffect(() => {
        const handleSearchHotkey = (event : KeyboardEvent) => {
            if ((!event.ctrlKey && !event.metaKey) || event.code !== 'KeyF' || !queryField.current) {
                return;
            }

            event.preventDefault();
            queryField.current.focus();
            window.requestAnimationFrame(() => window.scrollTo(0, 0));
        };

        window.addEventListener('keydown', handleSearchHotkey);

        return () => {
            window.removeEventListener('keydown', handleSearchHotkey);
        };
    }, []);

    return (
        <Paper className={classNames(classes.root, className)}>
            <Grid container>
                <Grid item xs={12} md={9}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <TextField
                                margin="dense"
                                value={startDateValue}
                                variant="outlined"
                                onChange={event => onStartDateChange(LocalDate.parse(event.target.value as string))}
                                select
                            >
                                {dateOptions.map(month => {
                                    return [
                                        <ListSubheader
                                            key={month.label}
                                            className={classes.listSubheader}
                                        >
                                            {month.label}
                                        </ListSubheader>,
                                        ...month.weeks.map(week => (
                                            <MenuItem key={week.value} value={week.value}>{week.label}</MenuItem>
                                        ))
                                    ];
                                })}
                            </TextField>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" onClick={() => onStartDateChange(firstDate.plusWeeks(9))}>
                                Current week
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" onClick={() => onStartDateChange(firstDate.plusWeeks(16))}>
                                Next 7 weeks
                            </Button>
                        </Grid>
                        <Grid item xs={12}/>
                        <Grid item>
                            <TextField
                                margin="dense"
                                variant="outlined"
                                value={query}
                                onChange={event => handleQueryChange(event.target.value)}
                                onKeyDown={handleQueryKeyDown}
                                inputRef={queryField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon/>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => handleQueryChange('')}
                                                disabled={query === ''}
                                                edge="end"
                                            >
                                                <ClearIcon/>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                margin="dense"
                                label="In"
                                value={filterSearch.type}
                                variant="outlined"
                                onChange={event => onFilterSearchChange({
                                    type: event.target.value as FilterSearchType,
                                    query: filterSearch.query,
                                })}
                                select
                            >
                                <MenuItem value="people">People</MenuItem>
                                <MenuItem value="projects">Projects</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </Grid>
                <Hidden smDown>
                    <Grid item md={3}>
                        <Grid item style={{justifyContent: 'flex-end', alignItems: 'stretch', display: 'grid'}}>
                            <TimeVarianceAuthority style={{maxHeight: '135px', maxWidth: '100%'}}/>
                        </Grid>
                    </Grid>
                </Hidden>
            </Grid>
        </Paper>
    );
};

export default ScheduleFilter;
